import { useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App } from 'antd';
import { Form, Formik, FormikValues } from 'formik';
import * as Yup from 'yup';

import { Button, Select } from '../../../custom';
import Input from '../../../custom/input/input';
import { StatusOptions, getCountry, getLGAByStateId, getStateByCountryId, updateInstitution } from '../../../requests';
import { validator } from '../../../utils/validator';

interface Props {
  handleClose: () => void;
  record: Institution;
}

interface InstitutionUpdatePayload {
  institutionName: string;
  address: string;
  contactPerson: string;
  email: string;
  phoneNumber: string;
  description: string;
  created: string;
  createdBy: string;
  activeStatus: boolean;
  stateId: number;
  countryId: number;
  lgaId: number;
}

const EditInstitution = ({ handleClose, record }: Props) => {
  const { notification } = App.useApp();
  const queryClient = useQueryClient();

  const [countryId, setCountryId] = useState<number | undefined>(record?.countryId ?? undefined);
  const [stateId, setStateId] = useState<number | undefined>(record?.stateId ?? undefined);

  const editInstitutionMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: InstitutionUpdatePayload }) =>
      updateInstitution(id, payload as unknown as Partial<Institution>),
  });

  const { data: country } = useQuery({
    queryKey: ['get-country'],
    queryFn: () => getCountry({ pageNumber: 1, pageSize: 500 }),
  });

  const { data: state, isLoading: isStateLoading } = useQuery({
    queryKey: ['get-state-by-country', countryId],
    queryFn: () => getStateByCountryId(Number(countryId)),
    enabled: Boolean(countryId),
  });

  const { data: lga, isLoading: isLgaLoading } = useQuery({
    queryKey: ['get-lga-by-state', stateId],
    queryFn: () => getLGAByStateId(Number(stateId)),
    enabled: Boolean(stateId),
  });

  const editInstitutionHandler = async (values: FormikValues, resetForm: () => void) => {
    const payload: InstitutionUpdatePayload = {
      institutionName: values.institutionName,
      address: values.address,
      contactPerson: values.contactPerson,
      email: values.email,
      phoneNumber: values.phoneNumber,
      description: values.description,
      created: record?.created,
      createdBy: record?.createdBy,
      activeStatus: values.activeStatus === 'true',
      stateId: Number(values.stateId),
      countryId: Number(values.countryId),
      lgaId: Number(values.lgaId),
    };

    try {
      await editInstitutionMutation.mutateAsync(
        {
          id: record?.id,
          payload,
        },
        {
          onSuccess: (data) => {
            notification.success({
              message: 'Success',
              description: data?.message,
            });

            queryClient.refetchQueries({ queryKey: ['get-institution'] });
            handleClose();
            resetForm();
          },
        },
      );
    } catch (error: any) {
      notification.error({
        message: 'Error',
        description: error?.response?.data?.message,
      });
    }
  };

  const countries = country?.data ?? [];
  const states = state?.data ?? [];
  const lgas = lga?.data ?? [];

  const countryOptions = countries?.map((item) => (
    <option key={item?.id} value={item?.id}>
      {item?.countryName}
    </option>
  ));

  const stateOptions = states?.map((item) => (
    <option key={item?.id} value={item?.id}>
      {item?.stateName}
    </option>
  ));

  const lgaOptions = lgas?.map((item) => (
    <option key={item?.id} value={item?.id}>
      {item?.lgaName}
    </option>
  ));

  const validationSchema = Yup.object().shape({
    institutionName: Yup.string().required('Institution name is required'),
    address: Yup.string().required('Address is required'),
    contactPerson: Yup.string().required('Contact person is required'),
    email: validator.email,
    phoneNumber: Yup.string().required('Phone number is required'),
    description: Yup.string().required('Description is required'),
    countryId: Yup.string().required('Country is required'),
    stateId: Yup.string().required('State is required'),
    lgaId: Yup.string().required('LGA is required'),
    activeStatus: Yup.string().required('Status is required'),
  });

  return (
    <Formik
      initialValues={{
        institutionName: record?.institutionName ?? '',
        address: record?.address ?? '',
        contactPerson: record?.contactPerson ?? '',
        email: record?.email ?? '',
        phoneNumber: record?.phoneNumber ?? '',
        description: record?.description ?? '',
        countryId: String(record?.countryId ?? ''),
        stateId: String(record?.stateId ?? ''),
        lgaId: String(record?.lgaId ?? ''),
        activeStatus: String(record?.activeStatus ?? ''),
      }}
      onSubmit={(values, { resetForm }) => {
        editInstitutionHandler(values, resetForm);
      }}
      validationSchema={validationSchema}
      enableReinitialize
    >
      {({ setFieldValue }) => {
        return (
          <Form className="fields">
            <Input label="Institution Name" placeholder="Input Institution Name" name="institutionName" />

            <Input label="Address" placeholder="Input Address" name="address" />

            <Input label="Contact Person" placeholder="Input Contact Person" name="contactPerson" />

            <Input label="Email" placeholder="Input Email" name="email" />

            <Input label="Phone Number" placeholder="Input Phone Number" name="phoneNumber" />

            <Input label="Description" placeholder="Input Description" name="description" />

            <Select
              name="countryId"
              placeholder="Select Country"
              label="Country"
              options={countryOptions}
              onChange={(event) => {
                const selectedCountryId = Number(event.target.value);
                setCountryId(selectedCountryId);
                setStateId(undefined);
                setFieldValue('stateId', '');
                setFieldValue('lgaId', '');
              }}
            />

            <Select
              name="stateId"
              placeholder="Select State"
              label="State"
              options={stateOptions}
              disabled={!countryId}
              loading={isStateLoading}
              onChange={(event) => {
                const selectedStateId = Number(event.target.value);
                setStateId(selectedStateId);
                setFieldValue('lgaId', '');
              }}
            />

            <Select
              name="lgaId"
              placeholder="Select LGA"
              label="LGA"
              options={lgaOptions}
              disabled={!stateId}
              loading={isLgaLoading}
            />

            <Select
              name="activeStatus"
              placeholder="Select Status"
              label="Status"
              options={
                <>
                  {StatusOptions.map((option: any) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </>
              }
            />

            <div className="btn-group">
              <Button onClick={handleClose} variant="text" text="Cancel" />
              <Button
                text={editInstitutionMutation.isPending ? 'Submitting...' : 'Submit'}
                type="submit"
                isLoading={editInstitutionMutation?.isPending}
                disabled={editInstitutionMutation?.isPending}
              />
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default EditInstitution;
